// /scb/vecka-<week>-<year> and /digest/:slug — one automated digest, public
// and indexable, the same way a hand-authored sample delivery is (see
// RapportvaktSamplePage.jsx, which this mirrors closely).
//
// A digest is not a delivery: it comes from scripts/export_digests.py, not
// from RAPPORTVAKT_DELIVERIES, and it carries the source items its rollup
// drew on instead of a single report PDF. The three points still render
// through the same <Delivery> component — see the deliveryView adapter
// below for the small shape gap between the two (a digest has no single
// reportPageUrl the way a PDF-backed delivery does).

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import RapportvaktShell, { useRvLang } from '../components/rapportvakt/Shell';
import RapportvaktNotFoundPage from './RapportvaktNotFoundPage';
import { Delivery, Glyph } from '../components/rapportvakt/Delivery';
import {
  RAPPORTVAKT_APP_PATH,
  RAPPORTVAKT_HELP_PATH,
  RAPPORTVAKT_PRICE,
  RAPPORTVAKT_SOURCE_COUNT,
  adjacentDigests,
  getDigestBySlug,
  getScbDigestByWeek,
  isoWeek,
  rapportvaktDigestPath,
} from '../config/rapportvakt';
import { getRapportvaktDigestSeo } from '../config/seoRoutes';

// "vecka-36-2026" -> { week: '36', year: '2026' }, or null when the segment
// isn't that shape. React Router 6 cannot match two params inside one path
// segment (see rapportvaktDigestPath's doc comment in rapportvakt.js), so
// the route is "/scb/:weekSlug" and this is the one place that parses it.
const WEEK_SLUG = /^vecka-(\d{1,2})-(\d{4})$/;

function resolveDigest(weekSlug, slug) {
  if (slug) return getDigestBySlug(slug);
  if (weekSlug) {
    const match = WEEK_SLUG.exec(weekSlug);
    if (!match) return null;
    return getScbDigestByWeek(match[1], match[2]);
  }
  return null;
}

export default function RapportvaktDigestPage() {
  const { weekSlug, slug } = useParams();
  const digest = resolveDigest(weekSlug, slug);

  // Not a redirect to "/", unlike RapportvaktSamplePage's unknown-slug case:
  // a URL of the /scb/vecka-N-YYYY/ shape that resolves to nothing (a typo,
  // a week that was never published, a week that hasn't happened yet) is
  // exactly what the 404 page is for, and it needs the real 404 HTTP status
  // a redirect target never carries.
  if (!digest) {
    return <RapportvaktNotFoundPage />;
  }

  const seo = getRapportvaktDigestSeo(digest);

  return (
    <RapportvaktShell seo={seo}>
      <RapportvaktDigestBody digest={digest} />
    </RapportvaktShell>
  );
}

// A child of RapportvaktShell, not RapportvaktDigestPage itself: useRvLang
// reads RvLangContext, which RapportvaktShell only provides to its own
// children — see RapportvaktPage.jsx's RapportvaktApp for the same split.
function RapportvaktDigestBody({ digest }) {
  const { t, lang } = useRvLang();
  const week = digest.windowStart ? isoWeek(digest.windowStart) : null;
  const { prev, next } = adjacentDigests(digest);
  const items = digest.items || [];

  // <Delivery> was built for RAPPORTVAKT_DELIVERIES entries, which carry a
  // single reportPageUrl (the PDF's own landing page). A digest has no such
  // page — it rolls up several source items — so this points at the first
  // item's URL instead, the closest thing to "where this week's reading
  // starts". Everything else Delivery reads (publisher, dateLabel, title,
  // points) is already the shape digests.js exports.
  const deliveryView = {
    publisher: digest.publisherName,
    dateLabel: digest.dateLabel || (week ? `vecka ${week.week}, ${week.year}` : digest.title),
    title: digest.title,
    reportPageUrl: items[0]?.url || 'https://www.scb.se/hitta-statistik/statistik-efter-amne/',
    points: digest.points,
  };

  return (
    <main className="rv__inner">
      <div className="rv-sample rv-digest">
        <p className="rv-sample__kicker">{t.weeklyNote}</p>

        <div className="rv-proof">
          <Delivery delivery={deliveryView} />
        </div>

        {items.length ? (
          <section className="rv-digest__items" aria-labelledby="rv-digest-items-head">
            <h2 className="rv-digest__itemsHead" id="rv-digest-items-head">
              {week ? t.weeklyAllWeek(week.week, items.length) : t.weeklyAll(items.length)}
            </h2>
            <ul className="rv-digest__itemsList">
              {items.map((item) => (
                <li key={item.url}>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {prev || next ? (
          <nav className="rv-digest__nav" aria-label={t.otherWeeks}>
            {prev ? (
              <Link className="rv-digest__navLink" to={rapportvaktDigestPath(prev)}>
                <Glyph name="arrow" size={12} /> {t.prevWeek}
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link className="rv-digest__navLink rv-digest__navLink--next" to={rapportvaktDigestPath(next)}>
                {t.nextWeek} <Glyph name="arrow" size={12} />
              </Link>
            ) : null}
          </nav>
        ) : null}

        <div className="rv-cta">
          <div>
            <h3 className="rv-cta__title">{t.ctaTitle}</h3>
            <p className="rv-cta__sub">
              {t.ctaBody(
                RAPPORTVAKT_SOURCE_COUNT,
                RAPPORTVAKT_PRICE.amount,
                lang === 'en' ? RAPPORTVAKT_PRICE.periodEn : RAPPORTVAKT_PRICE.period
              )}
              <Link to={RAPPORTVAKT_HELP_PATH}>{t.helpPageLink}</Link>
            </p>
          </div>
          <Link className="rv-btn rv-btn--primary" to={`${RAPPORTVAKT_APP_PATH}/`}>
            {t.heroCta}
            <Glyph name="arrow" size={13} />
          </Link>
        </div>
      </div>
    </main>
  );
}
